import { TruncatedTextWithTooltip } from '@app/components/base/TruncatedTextWithTooltip';
import { createActionsColumn } from '@app/modules/audit/components/create-actions-column';
import { createCustomIdColumn } from '@app/modules/audit/components/create-custom-id-column';
import { createStatusColumn } from '@app/modules/audit/components/create-status-column';
import { advertiserFieldMessage } from '@app/modules/audit/modules/station-audit/components/AdvertiserField';
import { campaignFieldMessage } from '@app/modules/audit/modules/station-audit/components/CampaignField';
import { mentionIdFieldMessage } from '@app/modules/audit/modules/station-audit/components/MentionIdField';
import { versionFieldMessage } from '@app/modules/audit/modules/station-audit/components/VersionField';
import { PntStationStatusItemDtoV2 } from '@mi-front/core';
import { MrtTable } from '@mi-front/ui';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RowActionMui } from '../../../../../../../../components/table/RowActionMui';
import { RowActions } from '../../../../../../../../components/table/RowActions';
import { ReportResultProps } from '../../../../../../../../core/reports/types';
import { getScheduleStatusCounts } from '../spot/get-schedule-status-counts';
import {
  getScheduleStatusKind,
  getScheduleStatusSortRank,
  matchesScheduleStatusFilter,
  ScheduleStatusFilterValue,
} from '../spot/get-schedule-status-kind';
import { scheduleStatusHeaderMessage } from '../spot/schedule-status-spot-messages';
import { uniqueScheduleField } from '../spot/schedule-status-spot-utils';
import { ScheduleStatusSpotOverview } from '../spot/ScheduleStatusSpotOverview';
import { ScheduleStatusSpotStatusChip } from '../spot/ScheduleStatusSpotStatusChip';
import {
  scheduleStatusMentionsHelpMessage,
  viewDetailActionMessage,
} from './schedule-status-mentions-messages';
import { getMentionsAdvertiser } from './schedule-status-mentions-utils';
import { ScheduleStatusMentionsDetailDialog } from './ScheduleStatusMentionsDetailDialog';
import { ScheduleStatusMentionsReport } from './ScheduleStatusMentionsView';

/**
 * Mentions schedule-status table. Scan columns: ID, coverage, advertiser, campaign, version.
 * Order, mention script, and per-slot matches live in the detail dialog.
 */
export function ScheduleStatusMentionsResult(p: ReportResultProps<ScheduleStatusMentionsReport>) {
  const { $t } = useIntl();
  const [detail, showDetail] = useState<PntStationStatusItemDtoV2 | null>(null);
  const [statusFilter, setStatusFilter] = useState<ScheduleStatusFilterValue | null>(null);
  const statusCounts = useMemo(() => getScheduleStatusCounts(p.result.items), [p.result.items]);
  const visibleItems = useMemo(
    () => p.result.items.filter((item) => matchesScheduleStatusFilter(item.status, statusFilter)),
    [p.result.items, statusFilter],
  );

  return (
    <>
      <ScheduleStatusSpotOverview
        counts={statusCounts}
        selected={statusFilter}
        onSelect={setStatusFilter}
        helpMessage={scheduleStatusMentionsHelpMessage}
      />
      <MrtTable
        data={visibleItems}
        enableTopToolbar={false}
        initialState={{
          sorting: [{ id: 'status', desc: false }],
        }}
        columns={[
          createCustomIdColumn<PntStationStatusItemDtoV2>($t, {
            accessor: (row) => row.customId,
            header: mentionIdFieldMessage,
          }),
          createStatusColumn<PntStationStatusItemDtoV2>($t, {
            header: scheduleStatusHeaderMessage,
            size: 220,
            enableSorting: true,
            accessorFn: (item) => getScheduleStatusKind(item.status),
            sortingFn: (rowA, rowB) =>
              getScheduleStatusSortRank(rowA.original.status) -
              getScheduleStatusSortRank(rowB.original.status),
            Cell: ({ row: { original: item } }) => (
              <ScheduleStatusSpotStatusChip status={item.status} onClick={() => showDetail(item)} />
            ),
          }),
          {
            id: 'advertiser',
            header: $t(advertiserFieldMessage),
            size: 170,
            enableSorting: true,
            accessorFn: (item) => getMentionsAdvertiser(item),
            Cell: ({ renderedCellValue }) => (
              <TruncatedTextWithTooltip value={renderedCellValue as string} />
            ),
          },
          {
            id: 'campaign',
            header: $t(campaignFieldMessage),
            size: 170,
            enableSorting: true,
            accessorFn: (item) => uniqueScheduleField(item.schedules ?? [], 'campaign'),
            Cell: ({ renderedCellValue }) => (
              <TruncatedTextWithTooltip value={renderedCellValue as string} />
            ),
          },
          {
            id: 'version',
            header: $t(versionFieldMessage),
            size: 200,
            enableSorting: true,
            accessorFn: (item) => uniqueScheduleField(item.schedules ?? [], 'version'),
            Cell: ({ renderedCellValue }) => (
              <TruncatedTextWithTooltip value={renderedCellValue as string} />
            ),
          },
          createActionsColumn<PntStationStatusItemDtoV2>($t, {
            size: 80,
            Cell: ({ row }) => (
              <RowActions>
                <RowActionMui
                  title={<FormattedMessage {...viewDetailActionMessage} />}
                  onClick={() => showDetail(row.original)}
                >
                  <InfoIcon />
                </RowActionMui>
              </RowActions>
            ),
          }),
        ]}
      />
      <ScheduleStatusMentionsDetailDialog item={detail} onHide={() => showDetail(null)} />
    </>
  );
}
